import { assertExhaustive } from '@fullstory/typeutils';

const normalizedAttributeNameBrand = Symbol();
export type NormalizedAttributeName = string & {
  [normalizedAttributeNameBrand]: true
};

export type AttributeKind = 'html' | 'svg';
export type TagKind = 'html' | 'svg';

// Given an untrusted attribute name, returns a version that has been normalized
// in the way that our security checks expect. The 'NormalizedAttributeName'
// type serves as proof that this normalization got done. This allows us to
// avoid normalizing attribute names in every function that needs to interact
// with them.
export function toNormalizedAttributeName(
  attributeName: string,
  kind: AttributeKind,
): NormalizedAttributeName {
  switch (kind) {
    case 'html':
      // HTML attributes are case-insensitive, so normalize them by converting
      // them to all lower-case.
      return attributeName.toLowerCase() as NormalizedAttributeName;

    case 'svg':
      // SVG attributes are case-sensitive, so consider them normalized as-is.
      return attributeName as NormalizedAttributeName;

    default:
      return assertExhaustive(kind, `Unexpected attribute kind '${kind}'`);
  }
}

const normalizedTagNameBrand = Symbol();
export type NormalizedTagName = keyof HTMLElementTagNameMap | string & {
  [normalizedTagNameBrand]: true
};

// Like toNormalizedAttributeName(), but for tag names. See that function's
// documentation for more context.
export function toNormalizedTagName(
  tagName: string,
  kind: TagKind = 'html',
): NormalizedTagName {
  switch (kind) {
    case 'html':
      // HTML tags are case-insensitive, so normalize them by converting
      // them to all lower-case.
      return tagName.toLowerCase() as NormalizedTagName;
    case 'svg':
      // SVG tags are case-sensitive, so consider them normalized as-is.
      return tagName as NormalizedTagName;

    default:
      return assertExhaustive(kind, `Unexpected tag kind '${kind}'`);
  }
}
